<div class="p-8 min-h-screen bg-white m-4 rounded-xl border border-gray-100 shadow-sm">
  <div class="mb-6">
    <div class="flex items-center justify-between mb-4 flex-wrap gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 mb-1">Insurance accounts</h1>
        <p class="text-sm text-gray-600">Create and manage accounts used on jobs and billing</p>
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          (click)="openCreateModal()"
          class="px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors flex items-center gap-2"
        >
          <i class="fas fa-plus"></i>
          Add account
        </button>
        <button
          type="button"
          (click)="loadAccounts()"
          class="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
          [disabled]="loading"
        >
          <i class="fas fa-sync-alt" [class.fa-spin]="loading"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="flex items-center gap-4 flex-wrap">
      <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
        <input
          type="checkbox"
          [(ngModel)]="includeInactive"
          (change)="onIncludeInactiveChange()"
          class="rounded border-gray-300 text-primary focus:ring-primary"
        />
        Show inactive
      </label>
      <div class="relative flex-1 min-w-[200px] max-w-md">
        <input
          type="text"
          [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()"
          placeholder="Search by name, account #, contact, email…"
          class="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-white"
        />
        <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
      </div>
    </div>
  </div>

  @if (successMessage) {
    <div class="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
      <div class="flex items-center gap-2">
        <i class="fas fa-check-circle text-green-600"></i>
        <p class="text-green-800">{{ successMessage }}</p>
      </div>
    </div>
  }

  @if (loading) {
    <div class="flex items-center justify-center py-12">
      <div class="text-center">
        <i class="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
        <p class="text-gray-600">Loading accounts…</p>
      </div>
    </div>
  }

  @if (error && !loading) {
    <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
      <div class="flex items-center gap-2">
        <i class="fas fa-exclamation-circle text-red-600"></i>
        <p class="text-red-800">{{ error }}</p>
      </div>
    </div>
  }

  @if (!loading) {
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Name</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Account #</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Contact</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Billing</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Pricing</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Active</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Updated</th>
              <th class="text-left py-4 px-6 text-sm font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            @if (filteredAccounts.length === 0) {
              <tr>
                <td colspan="8" class="py-12 text-center text-gray-500">
                  <i class="fas fa-building text-4xl mb-3 text-gray-300"></i>
                  <p class="text-lg font-medium">No accounts found</p>
                  @if (searchTerm) {
                    <p class="text-sm">Try a different search</p>
                  }
                </td>
              </tr>
            }
            @for (account of filteredAccounts; track account.id) {
              <tr class="hover:bg-gray-50 transition-colors">
                <td class="py-4 px-6">
                  <p class="text-sm font-medium text-gray-900">{{ account.name }}</p>
                </td>
                <td class="py-4 px-6 text-sm text-gray-600">{{ account.accountNumber || '—' }}</td>
                <td class="py-4 px-6 text-sm text-gray-600">{{ account.contactName || '—' }}</td>
                <td class="py-4 px-6 text-sm text-gray-600">
                  @if (account.billingEmail || account.billingPhone) {
                    <div>{{ account.billingEmail }}</div>
                    <div class="text-xs text-gray-500">{{ account.billingPhone }}</div>
                  } @else {
                    —
                  }
                </td>
                <td class="py-4 px-6 text-sm text-gray-600">
                  <div class="space-y-1">
                    <div>Hookup: {{ account.hookupFee | number:'1.2-2' }}</div>
                    <div class="space-y-0.5 text-xs">
                      <div>Office→pickup: {{ account.rateAB | number:'1.2-2' }}</div>
                      <div>Pickup→drop: {{ account.rateBC | number:'1.2-2' }}</div>
                      <div>Drop→office: {{ account.rateCA | number:'1.2-2' }}</div>
                    </div>
                    <div>Tax/Service: {{ account.taxPercent | number:'1.2-2' }}% / {{ account.serviceChargePercent | number:'1.2-2' }}%</div>
                  </div>
                </td>
                <td class="py-4 px-6">
                  <span
                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                    [ngClass]="{
                      'bg-green-100 text-green-800': account.isActive,
                      'bg-gray-100 text-gray-700': !account.isActive
                    }"
                  >
                    {{ account.isActive ? 'Yes' : 'No' }}
                  </span>
                </td>
                <td class="py-4 px-6 text-sm text-gray-600">{{ formatDate(account.updatedAt) }}</td>
                <td class="py-4 px-6">
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      (click)="openEditModal(account)"
                      class="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20"
                    >
                      <i class="fas fa-pen mr-1"></i> Edit
                    </button>
                    <button
                      type="button"
                      (click)="confirmDelete(account)"
                      class="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100"
                    >
                      <i class="fas fa-trash mr-1"></i> Delete
                    </button>
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  }
</div>

<!-- Create / Edit modal -->
@if (showModal) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40" (click)="closeModal()">
    <div
      class="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      (click)="$event.stopPropagation()"
    >
      <div class="p-6 border-b border-gray-200 flex items-center justify-between">
        <h2 class="text-lg font-semibold text-gray-900">
          {{ editingId === null ? 'New account' : 'Edit account' }}
        </h2>
        <button type="button" (click)="closeModal()" class="text-gray-500 hover:text-gray-700">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>
      <form [formGroup]="accountForm" (ngSubmit)="saveAccount()" class="p-6 space-y-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
          <input
            type="text"
            formControlName="name"
            class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
            [class.border-red-500]="accountForm.get('name')?.invalid && accountForm.get('name')?.touched"
          />
          @if (accountForm.get('name')?.invalid && accountForm.get('name')?.touched) {
            <p class="text-xs text-red-600 mt-1">Name is required</p>
          }
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Account #</label>
            <input type="text" formControlName="accountNumber" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Contact name</label>
            <input type="text" formControlName="contactName" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Billing email</label>
            <input type="email" formControlName="billingEmail" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            @if (accountForm.get('billingEmail')?.invalid && accountForm.get('billingEmail')?.touched) {
              <p class="text-xs text-red-600 mt-1">Invalid email</p>
            }
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Billing phone</label>
            <input type="text" formControlName="billingPhone" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Address line 1</label>
          <input type="text" formControlName="addressLine1" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Address line 2</label>
          <input type="text" formControlName="addressLine2" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
        </div>
        <div class="grid grid-cols-3 gap-4">
          <div class="col-span-1">
            <label class="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input type="text" formControlName="city" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input type="text" formControlName="state" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Postal code</label>
            <input type="text" formControlName="postalCode" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            formControlName="notes"
            rows="3"
            class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary"
          ></textarea>
        </div>
        <div class="border rounded-lg p-4">
          <h3 class="text-sm font-semibold text-gray-900 mb-3">Pricing profile</h3>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Hookup Fee</label>
              <input type="number" min="0" step="0.01" formControlName="hookupFee" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">$/mi, office → pickup</label>
              <input type="number" min="0" step="0.01" formControlName="rateAB" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">$/mi, pickup → drop-off</label>
              <input type="number" min="0" step="0.01" formControlName="rateBC" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">$/mi, drop-off → office</label>
              <input type="number" min="0" step="0.01" formControlName="rateCA" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Service Charge %</label>
              <input type="number" min="0" max="100" step="0.01" formControlName="serviceChargePercent" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tax %</label>
              <input type="number" min="0" max="100" step="0.01" formControlName="taxPercent" class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary" />
            </div>
          </div>
          <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer mt-3">
            <input type="checkbox" formControlName="isTaxExemptByDefault" class="rounded border-gray-300 text-primary focus:ring-primary" />
            Tax exempt by default
          </label>
        </div>
        <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" formControlName="isActive" class="rounded border-gray-300 text-primary focus:ring-primary" />
          Active
        </label>
        @if (error) {
          <div class="text-sm text-red-600">{{ error }}</div>
        }
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button type="button" (click)="closeModal()" class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="submitting"
            class="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            @if (submitting) {
              <i class="fas fa-spinner fa-spin mr-2"></i>
            }
            {{ editingId === null ? 'Create' : 'Save' }}
          </button>
        </div>
      </form>
    </div>
  </div>
}

<!-- Delete confirm -->
@if (deleteConfirmId !== null) {
  <div class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40">
    <div class="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
      <h3 class="text-lg font-semibold text-gray-900 mb-2">Delete account?</h3>
      <p class="text-sm text-gray-600 mb-6">
        This cannot be undone. If this account is used on jobs, consider deactivating it instead.
      </p>
      @if (error) {
        <div class="text-sm text-red-600 mb-4">{{ error }}</div>
      }
      <div class="flex justify-end gap-3">
        <button type="button" (click)="cancelDelete()" class="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          (click)="executeDelete()"
          [disabled]="submitting"
          class="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          @if (submitting) {
            <i class="fas fa-spinner fa-spin mr-2"></i>
          }
          Delete
        </button>
      </div>
    </div>
  </div>
}
